import { useMemo, useState } from 'react';

import { ChakraFlex, SkeletonLoader } from '@equip.health/ui';
import { DateTime } from 'luxon';

import ScheduleChangerV2, {
  type CalendarScheduleTabProps,
  GroupClassListTypeOptions,
  GroupClassWeekViewPagination,
} from '~/components/common/calendar/schedule/ScheduleChangerV2';
import {
  GroupClassListTab,
  GroupClassTabTitle,
} from '~/lib/constants/groupClasses.constants';
import { useGetGroupClassCountsQuery } from '~/store/services/groupClass';

import GroupClassListView from './GroupClassListView';
import GroupClassWeekView from './GroupClassWeekView';

const TOP_LEVEL_ID = 'calendar-group-class-wrapper';

// todo: This file will be removed when new group design is implemented.

const GroupClassWrapper = () => {
  const [currentDate, setCurrentDate] = useState<DateTime>(DateTime.now());
  const [groupClassTitle, setGroupClassTitle] = useState<string>('');
  const { data: groupClassCountResponse } = useGetGroupClassCountsQuery();

  const [listType, setListType] = useState<GroupClassListType>(
    GroupClassListTypeOptions.MY_GROUPS,
  );

  const [reloadThePage, setReloadThePage] = useState(false);

  const scheduleTabs = useMemo(
    () =>
      ({
        [GroupClassListTab.MY_GROUPS]: {
          count: groupClassCountResponse?.data?.myGroupsCount,
          listType: GroupClassListTypeOptions.MY_GROUPS,
          tabCount: groupClassCountResponse?.data?.myGroupsCount,
          title: GroupClassTabTitle.MY_GROUPS,
        },
        [GroupClassListTab.ACTIVE_GROUPS]: {
          count: groupClassCountResponse?.data?.activeGroupsCount,
          listType: GroupClassListTypeOptions.ACTIVE,
          tabCount: groupClassCountResponse?.data?.activeGroupsCount,
          title: GroupClassTabTitle.ACTIVE_GROUPS,
        },
        [GroupClassListTab.WEEKLY]: {
          listType: GroupClassListTypeOptions.WEEKLY,
          title: GroupClassTabTitle.WEEKLY_VIEW,
        },
        [GroupClassListTab.ARCHIVE]: {
          count: groupClassCountResponse?.data?.archivedGroupsCount,
          listType: GroupClassListTypeOptions.ARCHIVE,
          title: GroupClassTabTitle.ARCHIVE,
        },
      }) as Record<GroupClassListTab, CalendarScheduleTabProps>,
    [
      groupClassCountResponse?.data?.myGroupsCount,
      groupClassCountResponse?.data?.activeGroupsCount,
    ],
  );

  const resetReloadThePageValue = () => {
    setReloadThePage(false);
  };

  const renderGroupClasses = () => {
    switch (listType) {
      case GroupClassListTypeOptions.WEEKLY:
        return <GroupClassWeekView currentDate={currentDate} />;
      case GroupClassListTypeOptions.ACTIVE:
      case GroupClassListTypeOptions.ARCHIVE:
      case GroupClassListTypeOptions.MY_GROUPS:
        return (
          <GroupClassListView
            groupClassTitle={groupClassTitle}
            handleResetReload={resetReloadThePageValue}
            listType={listType}
            needReload={reloadThePage}
            tabs={scheduleTabs}
          />
        );
      default:
        return null;
    }
  };

  const handleWeekViewDateChange = (
    direction: GroupClassWeekViewPagination,
  ) => {
    if (direction == GroupClassWeekViewPagination.NEXT) {
      setCurrentDate(currentDate.plus({ days: 7 }));
    } else if (direction == GroupClassWeekViewPagination.PREVIOUS) {
      setCurrentDate(currentDate.minus({ days: 7 }));
    }
  };

  const fetchTheCountAndReloadGroupClasses = () => {
    setReloadThePage(true);
  };

  return (
    <ChakraFlex
      direction="column"
      height="100%"
      id={TOP_LEVEL_ID}
      overflow="hidden"
      paddingRight="0px"
    >
      {scheduleTabs ? (
        <>
          <ScheduleChangerV2
            currentDate={currentDate}
            groupClassTitle={groupClassTitle}
            onCreateClass={fetchTheCountAndReloadGroupClasses}
            onDateChange={handleWeekViewDateChange}
            onTabChanged={setListType}
            setGroupClassTitle={setGroupClassTitle}
            tabs={scheduleTabs}
          />
          {renderGroupClasses()}
        </>
      ) : (
        <SkeletonLoader
          id={`${TOP_LEVEL_ID}__loader`}
          isLoaded={scheduleTabs !== undefined}
        />
      )}
    </ChakraFlex>
  );
};

export default GroupClassWrapper;
